import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { API, BACKEND_URL, apiClient, useAuth } from '../context/AuthContext';
import GfMerger from '../components/GfMerger';
import PdfPreviewModal from '../components/PdfPreviewModal';
import AvisoValidacionModal from '../components/AvisoValidacionModal';
import { validarCertificaciones } from '../lib/certificaciones';
import { validarAccionesRealizadas } from '../lib/validarObligaciones';
import logger from '../lib/logger';
import { getErrorMessage } from '../lib/errorHandler';


const Dashboard = () => {
  const { showNotification, confirm, supportUser, isSupportMode, user } = useAuth();
  const isAdminOrSupport = user?.rol === 'admin' || isSupportMode;
  const [cuentas, setCuentas] = useState([]);
  const [postCuentas, setPostCuentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mostrarPapelera, setMostrarPapelera] = useState(false);
  const [papelera, setPapelera] = useState([]);
  const [loadingPapelera, setLoadingPapelera] = useState(false);
  const [activeTab, setActiveTab] = useState('GC');
  const [configuracion, setConfiguracion] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Escuchar cambios en la URL (parámetros de consulta)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab && (tab === 'GC' || tab === 'GF' || tab === 'PC')) {
      setActiveTab(tab);
    } else if (!tab) {
      // Si no hay parámetro tab (ej: navegar a "/" desde "/?tab=GF"), volver a GC
      setActiveTab('GC');
    }
  }, [location]);

  // Estados para Previsualización de Importación
  const [previewData, setPreviewData] = useState(null);
  const [previewType, setPreviewType] = useState(null); // 'cuenta' | 'post-cuenta'
  const [showPreview, setShowPreview] = useState(false);

  // Filtros de búsqueda
  const [searchText, setSearchText] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todos');

  // Estado de operaciones por tarjeta (e.g., { 'id123': 'enviando-pdf' })
  const [operacionEnCurso, setOperacionEnCurso] = useState({});

  // Estado del modal de vista previa de PDF
  const [previewConfig, setPreviewConfig] = useState(null);

  // ID de la tarjeta recién duplicada — para resaltarla (glow) y desplazarse a ella.
  const [tarjetaResaltada, setTarjetaResaltada] = useState(null);

  // Modal "Fusionar GC + IFPC firmado por el supervisor"
  const [mergeCuenta, setMergeCuenta] = useState(null); // cuenta del modal (o null)
  const [mergeFile, setMergeFile] = useState(null);     // PDF firmado seleccionado
  const [merging, setMerging] = useState(false);

  // Aviso emergente de validación (ej. faltan certificaciones para generar el GC)
  const [avisoValidacion, setAvisoValidacion] = useState(null);

  // Gate: los flujos que producen el PDF GC exigen "Acciones Realizadas" en
  // toda obligación con evidencias, y las 4 certificaciones cargadas (van
  // anexas después de las evidencias). Devuelve true si puede continuar; si
  // no, muestra el aviso indicando qué falta.
  const validarCertsAntesDeGC = (cuenta) => {
    const errAcc = validarAccionesRealizadas(cuenta.obligaciones || []);
    if (errAcc) {
      setAvisoValidacion({
        titulo: 'Faltan las Acciones Realizadas',
        mensaje: `${errAcc}\n(Cuenta de ${cuenta.mes_cobro || 'sin mes'} — abre "Editar" para escribirlas o generarlas con IA al guardar.)`,
      });
      return false;
    }
    const err = validarCertificaciones(cuenta.certificaciones || []);
    if (err) {
      setAvisoValidacion({
        titulo: 'Faltan certificaciones',
        mensaje: `${err}\n(Cuenta de ${cuenta.mes_cobro || 'sin mes'} — abre "Editar" para cargarlas.)`,
      });
      return false;
    }
    return true;
  };

  // Gate equivalente para POST CUENTAS (no llevan certificaciones).
  const validarAccionesPostCuenta = (postCuenta) => {
    const errAcc = validarAccionesRealizadas(postCuenta.obligaciones || []);
    if (errAcc) {
      setAvisoValidacion({
        titulo: 'Faltan las Acciones Realizadas',
        mensaje: `${errAcc}\n(Post Cuenta de ${postCuenta.mes_cobro || 'sin mes'} — abre "Editar" para escribirlas o generarlas con IA al guardar.)`,
      });
      return false;
    }
    return true;
  };

  // Resalta una tarjeta por id y hace scroll hacia ella. El glow dura ~3.5s.
  const resaltarYDesplazar = (id) => {
    if (!id) return;
    setTarjetaResaltada(id);
    setTimeout(() => {
      const el = document.querySelector(`[data-tarjeta-id="${id}"]`);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 180);
    setTimeout(() => setTarjetaResaltada(null), 3500);
  };

  const iniciarOperacion = (id, tipo) => setOperacionEnCurso(prev => ({ ...prev, [id]: tipo }));
  const finalizarOperacion = (id) => setOperacionEnCurso(prev => { const next = { ...prev }; delete next[id]; return next; });

  useEffect(() => {
    cargarDatos();
  }, [supportUser]);

  const cargarDatos = async () => {
    await Promise.all([cargarCuentas(), cargarPostCuentas(), cargarConfiguracion()]);
    setLoading(false);
  };

  const cargarConfiguracion = async () => {
    try {
      const response = await apiClient.get('/configuracion');
      setConfiguracion(response.data);
    } catch (error) {
      logger.error('Error cargando configuración:', error);
    }
  };

  const cargarCuentas = async () => {
    try {
      const response = await apiClient.get('/cuentas-cobro');
      setCuentas(response.data);
    } catch (error) {
      logger.error('Error cargando cuentas:', error);
    }
  };

  const cargarPostCuentas = async () => {
    try {
      const response = await apiClient.get('/post-cuentas');
      setPostCuentas(response.data);
    } catch (error) {
      logger.error('Error cargando post cuentas:', error);
    }
  };

  const cargarPapelera = async () => {
    setLoadingPapelera(true);
    try {
      const response = await apiClient.get('/papelera');
      setPapelera(response.data);
    } catch (error) {
      logger.error('Error cargando papelera:', error);
      showNotification(getErrorMessage(error, 'Error al cargar la papelera'), 'error');
    } finally {
      setLoadingPapelera(false);
    }
  };

  const abrirPapelera = () => {
    setMostrarPapelera(true);
    cargarPapelera();
  };

  const cerrarPapelera = () => {
    setMostrarPapelera(false);
  };

  const eliminarCuenta = async (id) => {
    const ok = await confirm({
      title: 'Mover a papelera',
      description: '¿Estás seguro de mover esta cuenta a la papelera?',
      confirmText: 'Mover a papelera',
      variant: 'destructive'
    });
    if (!ok) return;

    try {
      await axios.delete(`${API}/cuentas-cobro/${id}`);
      cargarCuentas();
      showNotification('Cuenta movida a la papelera. Tienes 30 días para restaurarla.', 'success');
    } catch (error) {
      logger.error('Error eliminando cuenta:', error);
      showNotification(getErrorMessage(error, 'Error al eliminar la cuenta de cobro'), 'error');
    }
  };

  const eliminarPostCuenta = async (id) => {
    const ok = await confirm({
      title: 'Eliminar Post Cuenta',
      description: '¿Estás seguro de eliminar esta Post Cuenta?',
      confirmText: 'Eliminar',
      variant: 'destructive'
    });
    if (!ok) return;

    try {
      await axios.delete(`${API}/post-cuentas/${id}`);
      cargarPostCuentas();
      showNotification('Post Cuenta eliminada exitosamente', 'success');
    } catch (error) {
      logger.error('Error eliminando post cuenta:', error);
      showNotification(getErrorMessage(error, 'Error al eliminar la Post Cuenta'), 'error');
    }
  };

  const restaurarCuenta = async (id) => {
    const ok = await confirm({
      title: 'Restaurar cuenta',
      description: '¿Restaurar esta cuenta de cobro?',
      confirmText: 'Restaurar'
    });
    if (!ok) return;

    try {
      await apiClient.post(`/papelera/${id}/restaurar`);
      // Actualizar inmediatamente la UI removiendo de papelera
      setPapelera(prevPapelera => prevPapelera.filter(c => c.id !== id));
      // Recargar cuentas para mostrar la restaurada
      cargarCuentas();
      showNotification('Cuenta restaurada exitosamente', 'success');
    } catch (error) {
      logger.error('Error restaurando cuenta:', error);
      showNotification(getErrorMessage(error, 'Error al restaurar la cuenta'), 'error');
      // Si falla, recargar papelera para mostrar estado correcto
      cargarPapelera();
    }
  };

  const eliminarPermanente = async (id) => {
    const ok = await confirm({
      title: 'Eliminar permanentemente',
      description: '¿ELIMINAR PERMANENTEMENTE esta cuenta? Esta acción no se puede deshacer.',
      confirmText: 'Eliminar para siempre',
      variant: 'destructive'
    });
    if (!ok) return;

    try {
      await apiClient.delete(`/papelera/${id}/eliminar-permanente`);
      // Actualizar inmediatamente la UI removiendo de papelera
      setPapelera(prevPapelera => prevPapelera.filter(c => c.id !== id));
      showNotification('Cuenta eliminada permanentemente', 'success');
    } catch (error) {
      logger.error('Error eliminando permanentemente:', error);
      showNotification(getErrorMessage(error, 'Error al eliminar la cuenta'), 'error');
      // Si falla, recargar papelera para mostrar estado correcto
      cargarPapelera();
    }
  };

  const descargarArchivoDirecto = async (url, nombreSugerido) => {
    // Si la URL empieza con /api, quitárselo porque apiClient ya tiene baseURL con /api
    const cleanUrl = url.startsWith('/api') ? url.replace('/api', '') : url;
    try {
      const response = await apiClient.get(cleanUrl, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', nombreSugerido);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      return true;
    } catch (error) {
      logger.error('Error en descarga directa:', error);
      showNotification(getErrorMessage(error, 'Error al descargar el archivo'), 'error');
      return false;
    }
  };

  const descargarPDF = (pdfUrl) => {
    const filename = pdfUrl.split('/').pop();
    descargarArchivoDirecto(pdfUrl, filename);
  };

  // Helper: llama al endpoint generador (que devuelve { pdf_url }), luego descarga ese PDF como blob
  // y crea un Object URL para mostrar en el iframe del modal de vista previa.
  // Retorna { url, filename } para que el modal pueda descargar el blob ya cargado sin regenerar.
  const cargarPdfBlob = async (generarFn) => {
    const genResp = await generarFn();
    const pdfUrl = genResp?.data?.pdf_url;
    if (!pdfUrl) throw new Error('No se recibió pdf_url del servidor');
    // apiClient ya tiene baseURL con /api, así que removemos el prefijo /api si existe
    const cleanUrl = pdfUrl.startsWith('/api') ? pdfUrl.replace('/api', '') : pdfUrl;
    const blobResp = await apiClient.get(cleanUrl, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([blobResp.data], { type: 'application/pdf' }));
    const filename = pdfUrl.split('/').pop();
    return { url, filename };
  };

  // Abrir vista previa para Cuenta de Cobro (con 3 tabs: GC, Solo Evidencias, GC+IFPC)
  const handlePreviewCuenta = (cuenta) => {
    if (!validarCertsAntesDeGC(cuenta)) return;
    const num = cuentas.length - cuentas.indexOf(cuenta);
    setPreviewConfig({
      title: `Cuenta de Cobro #${num} — ${cuenta.mes_cobro || 'Sin mes'}`,
      subtitle: `Contrato ${cuenta.numero_contrato || ''}`,
      tabs: [
        {
          key: 'gc',
          label: '📄 GC a PDF',
          loadBlobUrl: () => cargarPdfBlob(() => apiClient.post(`/cuentas-cobro/${cuenta.id}/generar-pdf`)),
          onDownload: async () => { await generarYDescargarPDF(cuenta.id); },
        },
        {
          key: 'evidencias',
          label: '📋 Solo Evidencias',
          loadBlobUrl: () => cargarPdfBlob(() => apiClient.post(`/cuentas-cobro/${cuenta.id}/generar-pdf-evidencias`)),
          onDownload: async () => { await generarPDFEvidencias(cuenta.id); },
        },
        {
          key: 'merge',
          label: '📄 GC+IFPC',
          loadBlobUrl: async () => {
            const resp = await apiClient.get(`/cuentas-cobro/${cuenta.id}/merge-gc-ifpc`, { responseType: 'blob' });
            const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
            // Intentar extraer filename del header Content-Disposition
            let filename = null;
            const disposition = resp.headers?.['content-disposition'] || '';
            const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (match && match[1]) {
              filename = match[1].replace(/['"]/g, '').trim();
            }
            if (!filename) {
              // Construir filename fallback basado en el mes_cobro
              const mes = (cuenta.mes_cobro || 'SIN_MES').replace(/ /g, '_').toUpperCase();
              filename = `GC-IFPC_${mes}.pdf`;
            }
            return { url, filename };
          },
          onDownload: async () => { await descargarMergeGcIfpc(cuenta); },
        },
      ],
    });
  };

  // Abrir vista previa para Post Cuenta (solo IFPC PDF)
  const handlePreviewPostCuenta = (postCuenta) => {
    if (!validarAccionesPostCuenta(postCuenta)) return;
    const num = postCuentas.length - postCuentas.indexOf(postCuenta);
    setPreviewConfig({
      title: `Post Cuenta #${num} — ${postCuenta.mes_cobro || 'Sin mes'}`,
      subtitle: `Contrato ${postCuenta.numero_contrato || ''}`,
      tabs: [
        {
          key: 'ifpc',
          label: '📄 IFPC a PDF',
          loadBlobUrl: () => cargarPdfBlob(() => apiClient.post(`/post-cuentas/${postCuenta.id}/generar-pdf`)),
          onDownload: async () => { await generarYDescargarPDFPostCuenta(postCuenta.id); },
        },
      ],
    });
  };

  const generarYDescargarPDF = async (cuentaId) => {
    const cuentaObj = cuentas.find(c => c.id === cuentaId);
    if (cuentaObj && !validarCertsAntesDeGC(cuentaObj)) return;
    iniciarOperacion(cuentaId, 'generando-pdf');
    try {
      const response = await apiClient.post(`/cuentas-cobro/${cuentaId}/generar-pdf`);
      if (response.data.pdf_url) {
        const filename = response.data.pdf_url.split('/').pop();
        const success = await descargarArchivoDirecto(response.data.pdf_url, filename);
        if (success) {
          showNotification('PDF generado y descargado exitosamente');
          cargarCuentas();
        }
      }
    } catch (error) {
      logger.error('Error generando PDF:', error);
      showNotification(getErrorMessage(error, 'Error al generar el PDF'), 'error');
    } finally {
      finalizarOperacion(cuentaId);
    }
  };

  const generarYDescargarPDFPostCuenta = async (postCuentaId) => {
    const pcObj = postCuentas.find(pc => pc.id === postCuentaId);
    if (pcObj && !validarAccionesPostCuenta(pcObj)) return;
    iniciarOperacion(postCuentaId, 'generando-pdf');
    try {
      const response = await apiClient.post(`/post-cuentas/${postCuentaId}/generar-pdf`);
      if (response.data.pdf_url) {
        const filename = response.data.pdf_url.split('/').pop();
        const success = await descargarArchivoDirecto(response.data.pdf_url, filename);
        if (success) {
          showNotification('PDF Post Cuenta generado y descargado exitosamente');
          cargarPostCuentas();
        }
      }
    } catch (error) {
      logger.error('Error generando PDF Post Cuenta:', error);
      showNotification(getErrorMessage(error, 'Error al generar el PDF de la Post Cuenta'), 'error');
    } finally {
      finalizarOperacion(postCuentaId);
    }
  };

  const generarPDFEvidencias = async (cuentaId) => {
    iniciarOperacion(cuentaId, 'generando-evidencias');
    try {
      const response = await apiClient.post(`/cuentas-cobro/${cuentaId}/generar-pdf-evidencias`);
      if (response.data.pdf_url) {
        const filename = response.data.pdf_url.split('/').pop();
        const success = await descargarArchivoDirecto(response.data.pdf_url, filename);
        if (success) {
          showNotification('PDF de evidencias generado y descargado exitosamente');
        }
      }
    } catch (error) {
      logger.error('Error generando PDF de evidencias:', error);
      showNotification(getErrorMessage(error, 'Error al generar el PDF de evidencias'), 'error');
    } finally {
      finalizarOperacion(cuentaId);
    }
  };

  // Modal de duplicado: pregunta si se copian también las evidencias o si las
  // obligaciones quedan en blanco para cargar las del nuevo mes.
  const [duplicarTarget, setDuplicarTarget] = useState(null); // { tipo:'cuenta'|'post', id, mes } | null
  const [duplicando, setDuplicando] = useState(false);

  const duplicarCuentaCobro = (cuentaId) => {
    const cuenta = cuentas.find(c => c.id === cuentaId);
    setDuplicarTarget({ tipo: 'cuenta', id: cuentaId, mes: cuenta?.mes_cobro || '' });
  };

  const duplicarPostCuenta = (postCuentaId) => {
    const pc = postCuentas.find(p => p.id === postCuentaId);
    setDuplicarTarget({ tipo: 'post', id: postCuentaId, mes: pc?.mes_cobro || '' });
  };

  const ejecutarDuplicado = async (incluirEvidencias) => {
    if (!duplicarTarget) return;
    const { tipo, id } = duplicarTarget;
    setDuplicando(true);
    iniciarOperacion(id, 'duplicando');
    try {
      const base = tipo === 'cuenta' ? 'cuentas-cobro' : 'post-cuentas';
      const response = await apiClient.post(`/${base}/${id}/duplicar`, null, {
        params: { incluir_evidencias: incluirEvidencias },
      });
      showNotification(
        incluirEvidencias
          ? `${tipo === 'cuenta' ? 'Cuenta' : 'Post cuenta'} duplicada con sus evidencias`
          : `${tipo === 'cuenta' ? 'Cuenta' : 'Post cuenta'} duplicada con las obligaciones en blanco`,
        'success'
      );
      setDuplicarTarget(null);
      if (tipo === 'cuenta') await cargarCuentas();
      else await cargarPostCuentas();
      resaltarYDesplazar(response.data?.id);
    } catch (error) {
      logger.error('Error duplicando:', error);
      showNotification(getErrorMessage(error, 'Error al duplicar'), 'error');
    } finally {
      setDuplicando(false);
      finalizarOperacion(id);
    }
  };

  const enviarCuentaPorEmail = async (cuentaId) => {
    iniciarOperacion(cuentaId, 'enviando-pdf');
    try {
      const response = await apiClient.post(`/cuentas-cobro/${cuentaId}/enviar-email`);
      showNotification(response.data.message, 'success');
    } catch (error) {
      logger.error('Error enviando cuenta por email:', error);
      showNotification(getErrorMessage(error, 'Error al enviar el correo'), 'error');
    } finally {
      finalizarOperacion(cuentaId);
    }
  };

  const enviarPostCuentaPorEmail = async (postCuentaId) => {
    iniciarOperacion(postCuentaId, 'enviando-pdf');
    try {
      const response = await apiClient.post(`/post-cuentas/${postCuentaId}/enviar-email`);
      showNotification(response.data.message, 'success');
    } catch (error) {
      logger.error('Error enviando post cuenta por email:', error);
      showNotification(getErrorMessage(error, 'Error al enviar el correo'), 'error');
    } finally {
      finalizarOperacion(postCuentaId);
    }
  };

  const enviarBackupCuentaPorEmail = async (cuentaId) => {
    iniciarOperacion(cuentaId, 'enviando-zip');
    try {
      const response = await apiClient.post(`/cuentas-cobro/${cuentaId}/enviar-backup-email`);
      showNotification(response.data.message, 'success');
    } catch (error) {
      logger.error('Error enviando backup por email:', error);
      showNotification(getErrorMessage(error, 'Error al enviar el backup'), 'error');
    } finally {
      finalizarOperacion(cuentaId);
    }
  };

  const descargarMergeGcIfpc = async (cuenta) => {
    iniciarOperacion(cuenta.id, 'descargando-merge');
    try {
      const response = await apiClient.get(
        `/cuentas-cobro/${cuenta.id}/merge-gc-ifpc`,
        { responseType: 'blob' }
      );
      const mes = cuenta.mes_cobro ? cuenta.mes_cobro.replace(/\s+/g, '_') : 'sin_mes';
      const filename = `GC-IFPC_${mes}.pdf`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      logger.error('Error descargando merge GC+IFPC:', error);
      let msg = 'Error al descargar GC+IFPC';
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          msg = json.detail || msg;
        } catch (_) {}
      } else {
        msg = getErrorMessage(error, msg);
      }
      showNotification(msg, 'error');
    } finally {
      finalizarOperacion(cuenta.id);
    }
  };

  // Cierra el modal de fusión con IFPC firmado (si no está fusionando).
  const cerrarModalMergeFirmado = () => {
    if (merging) return;
    setMergeCuenta(null);
    setMergeFile(null);
  };

  // Sube el IFPC firmado por el supervisor y lo fusiona con el GC (+evidencias).
  const fusionarConIfpcFirmado = async () => {
    if (!mergeFile || !mergeCuenta) return;
    setMerging(true);
    try {
      const formData = new FormData();
      formData.append('file', mergeFile);
      const response = await apiClient.post(
        `/cuentas-cobro/${mergeCuenta.id}/merge-gc-ifpc-upload`,
        formData,
        { responseType: 'blob' }
      );
      // Filename desde Content-Disposition, con fallback por mes
      let filename = null;
      const disposition = response.headers?.['content-disposition'] || '';
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) filename = match[1].replace(/['"]/g, '').trim();
      if (!filename) {
        // Fallback (si el navegador no expone Content-Disposition): armar la
        // nomenclatura GC_<cedula>_<rp>_<MES> desde la configuración.
        const mes = (mergeCuenta.mes_cobro || '').split(' de ')[0].toUpperCase() || 'SINMES';
        const cedula = configuracion?.cedula || 'SINCC';
        const rp = configuracion?.numero_rp || 'SINRP';
        filename = `GC_${cedula}_${rp}_${mes}.pdf`;
      }
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showNotification('GC + Informe Post Cuenta firmado fusionados y descargados.', 'success');
      setMergeCuenta(null);
      setMergeFile(null);
    } catch (error) {
      logger.error('Error fusionando GC+IFPC firmado:', error);
      let msg = 'Error al fusionar los PDFs';
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          msg = JSON.parse(text).detail || msg;
        } catch (_) {}
      } else {
        msg = getErrorMessage(error, msg);
      }
      showNotification(msg, 'error');
    } finally {
      setMerging(false);
    }
  };

  const enviarBackupPostCuentaPorEmail = async (postCuentaId) => {
    iniciarOperacion(postCuentaId, 'enviando-zip');
    try {
      const response = await apiClient.post(`/post-cuentas/${postCuentaId}/enviar-backup-email`);
      showNotification(response.data.message, 'success');
    } catch (error) {
      logger.error('Error enviando backup por email:', error);
      showNotification(getErrorMessage(error, 'Error al enviar el backup'), 'error');
    } finally {
      finalizarOperacion(postCuentaId);
    }
  };

  const bloquearCuenta = async (cuentaId) => {
    const ok = await confirm({
      title: 'Bloquear cuenta',
      description: '¿Bloquear esta cuenta? No podrás editarla hasta que la desbloquees.',
      confirmText: 'Bloquear'
    });
    if (!ok) return;

    iniciarOperacion(cuentaId, 'bloqueando');
    try {
      await apiClient.post(`/cuentas-cobro/${cuentaId}/bloquear`);
      showNotification('Cuenta bloqueada exitosamente', 'success');
      cargarCuentas();
    } catch (error) {
      logger.error('Error bloqueando cuenta:', error);
      showNotification(getErrorMessage(error, 'Error al bloquear la cuenta'), 'error');
    } finally {
      finalizarOperacion(cuentaId);
    }
  };

  const desbloquearCuenta = async (cuentaId) => {
    const ok = await confirm({
      title: 'Desbloquear cuenta',
      description: '¿Desbloquear esta cuenta para permitir ediciones?',
      confirmText: 'Desbloquear'
    });
    if (!ok) return;

    iniciarOperacion(cuentaId, 'desbloqueando');
    try {
      await apiClient.post(`/cuentas-cobro/${cuentaId}/desbloquear`);
      showNotification('Cuenta desbloqueada exitosamente', 'success');
      cargarCuentas();
    } catch (error) {
      logger.error('Error desbloqueando cuenta:', error);
      showNotification(getErrorMessage(error, 'Error al desbloquear la cuenta'), 'error');
    } finally {
      finalizarOperacion(cuentaId);
    }
  };

  const bloquearPostCuenta = async (postCuentaId) => {
    const ok = await confirm({
      title: 'Bloquear Post Cuenta',
      description: '¿Bloquear esta post cuenta? No podrás editarla hasta que la desbloquees.',
      confirmText: 'Bloquear'
    });
    if (!ok) return;

    iniciarOperacion(postCuentaId, 'bloqueando');
    try {
      await apiClient.post(`/post-cuentas/${postCuentaId}/bloquear`);
      showNotification('Post cuenta bloqueada exitosamente', 'success');
      cargarPostCuentas();
    } catch (error) {
      logger.error('Error bloqueando post cuenta:', error);
      showNotification(getErrorMessage(error, 'Error al bloquear la post cuenta'), 'error');
    } finally {
      finalizarOperacion(postCuentaId);
    }
  };

  const desbloquearPostCuenta = async (postCuentaId) => {
    const ok = await confirm({
      title: 'Desbloquear Post Cuenta',
      description: '¿Desbloquear esta post cuenta para permitir ediciones?',
      confirmText: 'Desbloquear'
    });
    if (!ok) return;

    iniciarOperacion(postCuentaId, 'desbloqueando');
    try {
      await apiClient.post(`/post-cuentas/${postCuentaId}/desbloquear`);
      showNotification('Post cuenta desbloqueada exitosamente', 'success');
      cargarPostCuentas();
    } catch (error) {
      logger.error('Error desbloqueando post cuenta:', error);
      showNotification(getErrorMessage(error, 'Error al desbloquear la post cuenta'), 'error');
    } finally {
      finalizarOperacion(postCuentaId);
    }
  };

  const handleEditarCuenta = (cuentaId, bloqueada, num) => {
    if (bloqueada) {
      showNotification('⚠️ Esta cuenta está bloqueada. Desbloquéala primero para poder editarla.', 'error');
      return;
    }
    // Si no está bloqueada, navegar a editar con el número asignado
    navigate(`/editar/${cuentaId}?n=${num}`);
  };

  const handleEditarPostCuenta = (postCuentaId, bloqueada, num) => {
    if (bloqueada) {
      showNotification('⚠️ Esta post cuenta está bloqueada. Desbloquéala primero para poder editarla.', 'error');
      return;
    }
    // Si no está bloqueada, navegar a editar con el número asignado
    navigate(`/post-cuentas/editar/${postCuentaId}?n=${num}`);
  };

  // --- Handlers de Respaldo (Backup) ---

  const handleExportCuenta = async (cuenta) => {
    iniciarOperacion(cuenta.id, 'exportando');
    try {
      const response = await apiClient.get(`/cuentas-cobro/${cuenta.id}/exportar-zip`, {
        responseType: 'blob'
      });

      const mes = cuenta.mes_cobro ? cuenta.mes_cobro.replace(/\s+/g, '_') : 'sin_mes';
      const filename = `backup_cuenta_${mes}_${new Date().toISOString().slice(0, 10)}.zip`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      logger.error('Error exportando cuenta:', error);
      showNotification(getErrorMessage(error, 'Error al exportar la cuenta (ZIP)'), 'error');
    } finally {
      finalizarOperacion(cuenta.id);
    }
  };

  const handleImportCuenta = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Si es un ZIP, usar el nuevo endpoint de importación total
    if (file.name.endsWith('.zip')) {
      handleImportZip(file);
      e.target.value = '';
      return;
    }

    // Mantener compatibilidad con JSON legacy
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const json = JSON.parse(event.target.result);

        // Validación básica de estructura
        if (!json.mes_cobro || !json.numero_contrato) {
          throw new Error('El archivo no tiene la estructura de una Cuenta de Cobro válida (faltan campos requeridos).');
        }

        // Preparar previsualización
        setPreviewData(json);
        setPreviewType('cuenta');
        setShowPreview(true);

      } catch (error) {
        logger.error('Error leyendo archivo:', error);
        showNotification(getErrorMessage(error, 'Error al leer el archivo'), 'error');
      }
    };
    reader.readAsText(file);
    e.target.value = ''; // Reset input
  };

  const handleImportZip = async (file) => {
    const ok = await confirm({
      title: 'Importar respaldo',
      description: `¿Deseas importar el respaldo "${file.name}"? Esto restaurará la cuenta y todos sus archivos adjuntos.`,
      confirmText: 'Importar'
    });
    if (!ok) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await apiClient.post('/importar-zip', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      showNotification(`✅ Respaldo importado exitosamente: ${response.data.message}`, 'success');

      if (response.data.tipo === 'cuenta') cargarCuentas();
      else if (response.data.tipo === 'post-cuenta') cargarPostCuentas();

    } catch (error) {
      logger.error('Error importando soporte ZIP:', error);
      showNotification(getErrorMessage(error, 'Error al importar el respaldo ZIP'), 'error');
    }
  };

  const handleExportPostCuenta = async (postCuenta) => {
    iniciarOperacion(postCuenta.id, 'exportando');
    try {
      const response = await apiClient.get(`/post-cuentas/${postCuenta.id}/exportar-zip`, {
        responseType: 'blob'
      });

      const mes = postCuenta.mes_cobro ? postCuenta.mes_cobro.replace(/\s+/g, '_') : 'sin_mes';
      const filename = `backup_post_cuenta_${mes}_${new Date().toISOString().slice(0, 10)}.zip`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      logger.error('Error exportando post cuenta:', error);
      showNotification(getErrorMessage(error, 'Error al exportar la post cuenta (ZIP)'), 'error');
    } finally {
      finalizarOperacion(postCuenta.id);
    }
  };

  const handleImportPostCuenta = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.name.endsWith('.zip')) {
      handleImportZip(file);
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const json = JSON.parse(event.target.result);

        // Validación básica de estructura
        if (!json.mes_cobro || !json.numero_contrato) {
          throw new Error('El archivo no tiene la estructura de una Post Cuenta válida (faltan campos requeridos).');
        }

        // Preparar previsualización
        setPreviewData(json);
        setPreviewType('post-cuenta');
        setShowPreview(true);

      } catch (error) {
        logger.error('Error leyendo archivo:', error);
        showNotification(getErrorMessage(error, 'Error al leer el archivo'), 'error');
      }
    };
    reader.readAsText(file);
    e.target.value = ''; // Reset input
  };

  const confirmImport = async () => {
    if (!previewData || !previewType) return;

    try {
      // Limpiar datos sensibles para crear copia nueva
      const { id, _id, user_id, fecha_creacion, ...cleanData } = previewData;

      if (previewType === 'cuenta') {
        await apiClient.post('/cuentas-cobro', cleanData);
        showNotification('✅ Cuenta de Cobro importada exitosamente.', 'success');
        cargarCuentas();
      } else if (previewType === 'post-cuenta') {
        await apiClient.post('/post-cuentas', cleanData);
        showNotification('✅ Post Cuenta importada exitosamente.', 'success');
        cargarPostCuentas();
      }

      // Cerrar modal y limpiar
      setShowPreview(false);
      setPreviewData(null);
      setPreviewType(null);

    } catch (error) {
      logger.error('Error confirmando importación:', error);
      showNotification(getErrorMessage(error, 'Error al importar'), 'error');
    }
  };

  const cancelImport = () => {
    setShowPreview(false);
    setPreviewData(null);
    setPreviewType(null);
  };


  // Listas filtradas (búsqueda + estado)
  const filteredCuentas = useMemo(() => {
    return cuentas.filter(cuenta => {
      const search = searchText.toLowerCase();
      const matchSearch = !search ||
        cuenta.mes_cobro?.toLowerCase().includes(search) ||
        cuenta.numero_contrato?.toLowerCase().includes(search);
      const matchEstado = filtroEstado === 'todos' ||
        (filtroEstado === 'completado' && cuenta.estado === 'completado') ||
        (filtroEstado === 'borrador' && cuenta.estado !== 'completado') ||
        (filtroEstado === 'bloqueada' && cuenta.bloqueada);
      return matchSearch && matchEstado;
    });
  }, [cuentas, searchText, filtroEstado]);

  const filteredPostCuentas = useMemo(() => {
    return postCuentas.filter(pc => {
      const search = searchText.toLowerCase();
      const matchSearch = !search ||
        pc.mes_cobro?.toLowerCase().includes(search) ||
        pc.numero_contrato?.toLowerCase().includes(search);
      const matchEstado = filtroEstado === 'todos' ||
        (filtroEstado === 'completado' && pc.estado === 'completado') ||
        (filtroEstado === 'borrador' && pc.estado !== 'completado') ||
        (filtroEstado === 'bloqueada' && pc.bloqueada);
      return matchSearch && matchEstado;
    });
  }, [postCuentas, searchText, filtroEstado]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-4"></div>
        <div className="text-lg font-semibold text-blue-900">Cargando datos...</div>
        <p className="text-sm text-blue-600 mt-1">Preparando tu panel de control</p>
      </div>
    );
  }

  return (
    <div className="fade-in">
      <div className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-blue-900 mb-2">
            {activeTab === 'GC' ? 'Cuentas de Cobro' : activeTab === 'PC' ? 'Informes Post Cuenta' : 'Gestión Financiera'}
          </h1>
          <p className="text-blue-700">
            {activeTab === 'GC' ? 'Informes mensuales de ejecución contractual' : activeTab === 'PC' ? 'Informes del IMEC posteriores al cobro' : 'Fusión de archivos PDF para soportes financieros'}
          </p>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="flex border-b mb-8 gap-1 bg-white p-1 rounded-t-xl shadow-sm overflow-x-auto">
        <button
          onClick={() => { setActiveTab('GC'); setSearchText(''); setFiltroEstado('todos'); }}
          className={`px-8 py-3 font-bold text-sm sm:text-base transition-all duration-200 flex items-center gap-2 whitespace-nowrap ${activeTab === 'GC'
            ? 'bg-blue-600 text-white rounded-lg shadow-lg transform translate-y-[-2px]'
            : 'text-blue-600 hover:bg-blue-50 rounded-lg'
            }`}
        >
          📋 Cuentas de Cobro (GC)
        </button>
        <button
          onClick={() => { setActiveTab('GF'); setSearchText(''); setFiltroEstado('todos'); }}
          className={`px-8 py-3 font-bold text-sm sm:text-base transition-all duration-200 flex items-center gap-2 whitespace-nowrap ${activeTab === 'GF'
            ? 'bg-indigo-600 text-white rounded-lg shadow-lg transform translate-y-[-2px]'
            : 'text-indigo-600 hover:bg-indigo-50 rounded-lg'
            }`}
        >
          🧩 Gestión Financiera (GF)
        </button>
        <button
          onClick={() => { setActiveTab('PC'); setSearchText(''); setFiltroEstado('todos'); }}
          className={`px-8 py-3 font-bold text-sm sm:text-base transition-all duration-200 flex items-center gap-2 whitespace-nowrap ${activeTab === 'PC'
            ? 'bg-purple-600 text-white rounded-lg shadow-lg transform translate-y-[-2px]'
            : 'text-purple-600 hover:bg-purple-50 rounded-lg'
            }`}
        >
          📝 Post Cuentas (PC)
        </button>
      </div>

      {activeTab === 'GC' ? (
        <>
          <div className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="flex flex-wrap gap-2 w-full md:w-auto">
              {isAdminOrSupport && (
              <label className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 font-medium transition cursor-pointer flex items-center gap-2" title="Importar respaldo (ZIP o JSON)">
                📤 Importar
                <input
                  type="file"
                  accept=".zip,.json"
                  onChange={handleImportCuenta}
                  className="hidden"
                />
              </label>
              )}
              <button
                onClick={abrirPapelera}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium transition flex items-center gap-2"
              >
                🗑️ Papelera
              </button>
            </div>
          </div>

          {/* Barra de filtros GC */}
          <div className="mb-6 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center bg-white p-3 rounded-xl shadow-sm">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
              <input
                type="text"
                placeholder="Buscar por mes o contrato..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
              />
              {searchText && (
                <button
                  onClick={() => setSearchText('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
                >
                  ✕
                </button>
              )}
            </div>
            <select
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="todos">Todos los estados</option>
              <option value="completado">Completados</option>
              <option value="borrador">Borradores</option>
              <option value="bloqueada">Bloqueadas</option>
            </select>
            {(searchText || filtroEstado !== 'todos') && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">
                  {filteredCuentas.length} de {cuentas.length}
                </span>
                <button
                  onClick={() => { setSearchText(''); setFiltroEstado('todos'); }}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  Limpiar
                </button>
              </div>
            )}
          </div>

          {cuentas.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <div className="text-6xl mb-4">📄</div>
              <h2 className="text-2xl font-semibold text-gray-700 mb-2">No hay cuentas de cobro</h2>
              <p className="text-gray-500 mb-6">Comienza creando tu primera cuenta de cobro</p>
              <Link
                to="/nueva"
                className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition"
              >
                + Crear Primera Cuenta
              </Link>
            </div>
          ) : filteredCuentas.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <div className="text-4xl mb-3">🔍</div>
              <h3 className="text-lg font-semibold text-gray-600 mb-1">Sin resultados</h3>
              <p className="text-gray-400 text-sm">No se encontraron cuentas con los filtros aplicados</p>
              <button
                onClick={() => { setSearchText(''); setFiltroEstado('todos'); }}
                className="mt-3 text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Limpiar filtros
              </button>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredCuentas.map((cuenta, index) => (
                <div
                  key={cuenta.id}
                  data-tarjeta-id={cuenta.id}
                  className={`bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 p-6 ${
                    tarjetaResaltada === cuenta.id
                      ? 'ring-4 ring-emerald-400 shadow-[0_0_25px_rgba(16,185,129,0.6)] scale-[1.02]'
                      : operacionEnCurso[cuenta.id] ? 'ring-2 ring-blue-300 ring-opacity-50' : ''
                  }`}
                >
                  <div className="flex flex-col gap-2 mb-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="flex items-center justify-center bg-blue-600 text-white text-[10px] font-bold px-2 py-1 rounded-md h-6 min-w-[28px]">
                        #{cuentas.length - cuentas.indexOf(cuenta)}
                      </span>
                      <h3 className="text-lg font-semibold text-blue-900 leading-none">{cuenta.mes_cobro}</h3>
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <div className="flex justify-between items-center gap-2 w-full">
                        <p className="text-sm text-gray-500 font-medium leading-none">{cuenta.fecha_diligenciamiento}</p>

                        <div className="flex items-center gap-1.5">
                          {cuenta.bloqueada && (
                            <span className="flex items-center gap-1 bg-orange-100 text-orange-800 text-[9px] font-bold px-2 py-0.5 rounded-md border border-orange-200 uppercase tracking-wider h-5 whitespace-nowrap">
                              🔒 Bloqueada
                            </span>
                          )}

                          <span
                            className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-bold border uppercase tracking-wider h-5 whitespace-nowrap ${cuenta.estado === 'completado'
                              ? 'bg-green-100 text-green-800 border-green-200'
                              : 'bg-yellow-100 text-yellow-800 border-yellow-200'
                              }`}
                          >
                            {cuenta.estado === 'completado' ? '✓ Completado' : '⏳ Borrador'}
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-x-4 gap-y-1">
                        <p className="text-xs text-gray-400">
                          📅 Creada: {new Date(cuenta.fecha_creacion).toLocaleDateString('es-CO', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                        {cuenta.fecha_actualizacion && (
                          <p className="text-xs text-blue-400 flex items-center gap-1">
                            <span>🔄</span> Modificada: {new Date(cuenta.fecha_actualizacion).toLocaleDateString('es-CO', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm text-gray-600 mb-4">
                    <p>
                      <strong>Contrato:</strong> {cuenta.numero_contrato}
                    </p>
                    {cuenta.numero_planilla && (
                      <p>
                        <strong>Planilla SOI:</strong> {cuenta.numero_planilla}
                      </p>
                    )}
                    {cuenta.periodo_planilla && (
                      <p>
                        <strong>Periodo Planilla:</strong> {cuenta.periodo_planilla}
                      </p>
                    )}
                    <p className="flex items-center gap-2">
                      <strong>📸 Evidencias:</strong>
                      <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full font-bold text-xs">
                        {cuenta.obligaciones?.reduce((acc, obl) => acc + (obl.evidencias?.length || 0), 0) || 0} cargadas
                      </span>
                    </p>
                  </div>

                  {operacionEnCurso[cuenta.id] && (
                    <div className="flex items-center gap-2 mb-2 px-3 py-1.5 bg-blue-50 rounded-lg">
                      <span className="w-3.5 h-3.5 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin"></span>
                      <span className="text-xs text-blue-700 font-medium">
                        {operacionEnCurso[cuenta.id] === 'enviando-pdf' ? 'Enviando PDF por correo...' :
                         operacionEnCurso[cuenta.id] === 'exportando' ? 'Exportando respaldo...' :
                         operacionEnCurso[cuenta.id] === 'generando-pdf' ? 'Generando PDF...' :
                         operacionEnCurso[cuenta.id] === 'generando-evidencias' ? 'Generando PDF de evidencias...' :
                         operacionEnCurso[cuenta.id] === 'duplicando' ? 'Duplicando cuenta...' :
                         operacionEnCurso[cuenta.id] === 'bloqueando' ? 'Bloqueando cuenta...' :
                         operacionEnCurso[cuenta.id] === 'desbloqueando' ? 'Desbloqueando cuenta...' :
                         operacionEnCurso[cuenta.id] === 'descargando-merge' ? 'Generando PDF combinado GC+IFPC...' : 'Procesando...'}
                      </span>
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
                    <button
                      onClick={() => handleEditarCuenta(cuenta.id, cuenta.bloqueada, cuentas.length - cuentas.indexOf(cuenta))}
                      className="flex-1 min-w-[80px] px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition text-sm"
                    >
                      ✏️ Editar
                    </button>
                    <button
                      onClick={() => duplicarCuentaCobro(cuenta.id)}
                      disabled={!!operacionEnCurso[cuenta.id]}
                      className="flex-1 min-w-[90px] px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium transition text-sm disabled:opacity-50"
                      title="Duplicar esta cuenta"
                    >
                      {operacionEnCurso[cuenta.id] === 'duplicando' ? '⏳' : '📋'} Duplicar
                    </button>
                    {isAdminOrSupport && (
                    <button
                      onClick={() => handleExportCuenta(cuenta)}
                      disabled={!!operacionEnCurso[cuenta.id]}
                      className="flex-1 min-w-[40px] px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition text-sm disabled:opacity-50"
                      title="Exportar respaldo completo (ZIP con anexos)"
                    >
                      {operacionEnCurso[cuenta.id] === 'exportando' ? '⏳' : '⬇️'}
                    </button>
                    )}
                    {cuenta.bloqueada ? (
                      <button
                        onClick={() => desbloquearCuenta(cuenta.id)}
                        disabled={!!operacionEnCurso[cuenta.id]}
                        className="flex-1 min-w-[100px] px-3 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 font-medium transition text-sm disabled:opacity-50"
                        title="Desbloquear para editar"
                      >
                        {operacionEnCurso[cuenta.id] === 'desbloqueando' ? '⏳' : '🔓'} Desbloquear
                      </button>
                    ) : (
                      <button
                        onClick={() => bloquearCuenta(cuenta.id)}
                        disabled={!!operacionEnCurso[cuenta.id]}
                        className="flex-1 min-w-[90px] px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium transition text-sm disabled:opacity-50"
                        title="Bloquear para evitar ediciones"
                      >
                        {operacionEnCurso[cuenta.id] === 'bloqueando' ? '⏳' : '🔒'} Bloquear
                      </button>
                    )}
                    <button
                      onClick={() => generarYDescargarPDF(cuenta.id)}
                      disabled={!!operacionEnCurso[cuenta.id]}
                      className="flex-1 min-w-[90px] px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition text-sm disabled:opacity-50"
                      title="Generar PDF de la Cuenta de Cobro"
                    >
                      {operacionEnCurso[cuenta.id] === 'generando-pdf' ? '⏳' : <><svg className="inline w-4 h-4 mr-1 -mt-0.5" viewBox="0 0 24 24" fill="currentColor"><path d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8.5 7.5c0 .83-.67 1.5-1.5 1.5H9v2H7.5V7H10c.83 0 1.5.67 1.5 1.5v1zm5 2c0 .83-.67 1.5-1.5 1.5h-2.5V7H15c.83 0 1.5.67 1.5 1.5v3zm4-3H19v1h1.5V11H19v2h-1.5V7h3v1.5zM9 9.5h1v-1H9v1zM4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm10 5.5h1v-3h-1v3z"/></svg></>} GC a PDF
                    </button>
                    <button
                      onClick={() => { if (!validarCertsAntesDeGC(cuenta)) return; setMergeCuenta(cuenta); setMergeFile(null); }}
                      disabled={!!operacionEnCurso[cuenta.id]}
                      className="flex-1 min-w-[90px] px-3 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 font-medium transition text-sm disabled:opacity-50"
                      title="Fusionar la Cuenta de Cobro con el Informe Post-Cuenta firmado por el supervisor"
                    >
                      <><svg className="inline w-4 h-4 mr-1 -mt-0.5" viewBox="0 0 24 24" fill="currentColor"><path d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8.5 7.5c0 .83-.67 1.5-1.5 1.5H9v2H7.5V7H10c.83 0 1.5.67 1.5 1.5v1zm5 2c0 .83-.67 1.5-1.5 1.5h-2.5V7H15c.83 0 1.5.67 1.5 1.5v3zm4-3H19v1h1.5V11H19v2h-1.5V7h3v1.5zM9 9.5h1v-1H9v1zM4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm10 5.5h1v-3h-1v3z"/></svg></> GC+IFPC
                    </button>
                    <button
                      onClick={() => generarPDFEvidencias(cuenta.id)}
                      disabled={!!operacionEnCurso[cuenta.id]}
                      className="flex-1 min-w-[120px] px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium transition text-sm disabled:opacity-50"
                      title="Generar PDF solo con evidencias"
                    >
                      {operacionEnCurso[cuenta.id] === 'generando-evidencias' ? '⏳' : '📋'} Solo Evidencias
                    </button>
                    <button
                      onClick={() => handlePreviewCuenta(cuenta)}
                      disabled={!!operacionEnCurso[cuenta.id]}
                      className="flex-1 min-w-[110px] px-3 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 font-medium transition text-sm disabled:opacity-50"
                      title="Vista previa del PDF"
                    >
                      👁️ Vista Previa
                    </button>
                    <button
                      onClick={() => eliminarCuenta(cuenta.id)}
                      disabled={!!operacionEnCurso[cuenta.id]}
                      className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition text-sm disabled:opacity-50"
                    >
                      🗑️ Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}



          {/* Modal de Papelera */}
          {mostrarPapelera && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col">
                <div className="p-6 border-b flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">🗑️ Papelera de Reciclaje</h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Las cuentas se eliminan automáticamente después de 30 días
                    </p>
                  </div>
                  <button
                    onClick={cerrarPapelera}
                    className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                  >
                    ×
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                  {loadingPapelera ? (
                    <div className="flex flex-col items-center justify-center py-12">
                      <div className="w-8 h-8 border-4 border-gray-200 border-t-gray-600 rounded-full animate-spin mb-3"></div>
                      <div className="text-sm text-gray-600 font-medium">Cargando papelera...</div>
                    </div>
                  ) : papelera.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="text-6xl mb-4">✨</div>
                      <h3 className="text-xl font-semibold text-gray-700 mb-2">Papelera vacía</h3>
                      <p className="text-gray-500">No hay cuentas eliminadas</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {papelera.map((cuenta, index) => (
                        <div
                          key={cuenta.id}
                          className="bg-gray-50 rounded-lg border border-gray-200 p-4 hover:bg-gray-100 transition"
                        >
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="inline-block bg-red-600 text-white text-xs font-bold px-2 py-1 rounded">
                                  #{papelera.length - index}
                                </span>
                                <h3 className="text-lg font-semibold text-gray-900">{cuenta.mes_cobro}</h3>
                              </div>
                              <p className="text-sm text-gray-500 mb-1">{cuenta.fecha_diligenciamiento}</p>
                              <div className="space-y-1 text-xs text-gray-500">
                                <p>
                                  📅 <strong>Creada:</strong> {new Date(cuenta.fecha_creacion).toLocaleDateString('es-CO', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                                <p>
                                  🗑️ <strong>Eliminada:</strong> {new Date(cuenta.deleted_at).toLocaleDateString('es-CO', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              </div>
                              <p className="text-xs text-red-600 font-semibold mt-2">
                                ⏳ Se eliminará permanentemente en {cuenta.dias_restantes} días
                              </p>
                            </div>
                          </div>

                          <div className="space-y-1 text-sm text-gray-600 mb-4">
                            <p>
                              <strong>Contrato:</strong> {cuenta.numero_contrato}
                            </p>
                          </div>

                          <div className="flex gap-2">
                            <button
                              onClick={() => restaurarCuenta(cuenta.id)}
                              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition"
                            >
                              ♻️ Restaurar
                            </button>
                            <button
                              onClick={() => eliminarPermanente(cuenta.id)}
                              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition"
                            >
                              🗑️ Eliminar Permanentemente
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="p-4 border-t bg-gray-50">
                  <button
                    onClick={cerrarPapelera}
                    className="w-full px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 font-medium transition"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Modal de Previsualización de Importación */}
          {showPreview && previewData && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full p-6 animate-fade-in-up">
                <h2 className="text-2xl font-bold text-blue-900 mb-4">
                  📤 Confirmar Importación
                </h2>

                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-3xl">
                      {previewType === 'cuenta' ? '📄' : '📋'}
                    </span>
                    <div>
                      <h3 className="font-semibold text-lg text-blue-900">
                        {previewType === 'cuenta' ? 'Cuenta de Cobro' : 'Post Cuenta'}
                      </h3>
                      <p className="text-sm text-blue-600">
                        Se importará como una copia nueva
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm text-gray-700">
                    <p><strong>Mes de Cobro:</strong> {previewData.mes_cobro}</p>
                    <p><strong>Contrato:</strong> {previewData.numero_contrato}</p>
                    <p><strong>Fecha Original:</strong> {new Date(previewData.fecha_creacion).toLocaleDateString()}</p>
                    {previewData.obligaciones && (
                      <p><strong>Obligaciones:</strong> {previewData.obligaciones.length} ítems</p>
                    )}
                  </div>
                </div>

                <div className="flex gap-4">
                  <button
                    onClick={cancelImport}
                    className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium transition"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={confirmImport}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition shadow-lg"
                  >
                    ✅ Confirmar Importación
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : activeTab === 'GF' ? (
        <GfMerger
          configuracion={configuracion}
          mesDefault={cuentas.length > 0 ? cuentas[0].mes_cobro : ''}
          cuentas={cuentas}
        />
      ) : (
        <>
          <div className="mb-8 flex justify-between items-center">
            <div className="flex flex-wrap gap-2 w-full md:w-auto">
              {isAdminOrSupport && (
              <label className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 font-medium transition cursor-pointer flex items-center gap-2" title="Importar respaldo (ZIP o JSON)">
                📤 Importar
                <input
                  type="file"
                  accept=".zip,.json"
                  onChange={handleImportPostCuenta}
                  className="hidden"
                />
              </label>
              )}
            </div>
          </div>

          {/* Barra de filtros PC */}
          <div className="mb-6 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center bg-white p-3 rounded-xl shadow-sm">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
              <input
                type="text"
                placeholder="Buscar por mes o contrato..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
              />
              {searchText && (
                <button
                  onClick={() => setSearchText('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
                >
                  ✕
                </button>
              )}
            </div>
            <select
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400"
            >
              <option value="todos">Todos los estados</option>
              <option value="completado">Completados</option>
              <option value="borrador">Borradores</option>
              <option value="bloqueada">Bloqueadas</option>
            </select>
            {(searchText || filtroEstado !== 'todos') && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">
                  {filteredPostCuentas.length} de {postCuentas.length}
                </span>
                <button
                  onClick={() => { setSearchText(''); setFiltroEstado('todos'); }}
                  className="text-xs text-purple-600 hover:text-purple-800 font-medium"
                >
                  Limpiar
                </button>
              </div>
            )}
          </div>

          {postCuentas.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <div className="text-6xl mb-4">📋</div>
              <h2 className="text-2xl font-semibold text-gray-700 mb-2">No hay Post Cuentas</h2>
              <p className="text-gray-500 mb-6">Comienza creando tu primera Post Cuenta</p>
              <Link
                to="/post-cuentas/nueva"
                className="inline-block px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium transition"
              >
                + Crear Primera Post Cuenta
              </Link>
            </div>
          ) : filteredPostCuentas.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <div className="text-4xl mb-3">🔍</div>
              <h3 className="text-lg font-semibold text-gray-600 mb-1">Sin resultados</h3>
              <p className="text-gray-400 text-sm">No se encontraron post cuentas con los filtros aplicados</p>
              <button
                onClick={() => { setSearchText(''); setFiltroEstado('todos'); }}
                className="mt-3 text-sm text-purple-600 hover:text-purple-800 font-medium"
              >
                Limpiar filtros
              </button>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredPostCuentas.map((postCuenta, index) => (
                <div
                  key={postCuenta.id}
                  data-tarjeta-id={postCuenta.id}
                  className={`bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 p-6 border-l-4 border-purple-500 ${
                    tarjetaResaltada === postCuenta.id
                      ? 'ring-4 ring-emerald-400 shadow-[0_0_25px_rgba(16,185,129,0.6)] scale-[1.02]'
                      : operacionEnCurso[postCuenta.id] ? 'ring-2 ring-purple-300 ring-opacity-50' : ''
                  }`}
                >
                  <div className="flex flex-col gap-2 mb-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="flex items-center justify-center bg-purple-600 text-white text-[10px] font-bold px-2 py-1 rounded-md h-6 min-w-[32px]">
                        #PC-{postCuentas.length - postCuentas.indexOf(postCuenta)}
                      </span>
                      <h3 className="text-lg font-semibold text-purple-900 leading-none">{postCuenta.mes_cobro}</h3>
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <div className="flex justify-between items-center gap-2 w-full">
                        <p className="text-sm text-gray-500 font-medium leading-none">{postCuenta.fecha_diligenciamiento}</p>

                        <div className="flex items-center gap-1.5">
                          {postCuenta.bloqueada && (
                            <span className="flex items-center gap-1 bg-orange-100 text-orange-800 text-[9px] font-bold px-2 py-0.5 rounded-md border border-orange-200 uppercase tracking-wider h-5 whitespace-nowrap">
                              🔒 Bloqueada
                            </span>
                          )}

                          <span
                            className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-bold border uppercase tracking-wider h-5 whitespace-nowrap ${postCuenta.estado === 'completado'
                              ? 'bg-green-100 text-green-800 border-green-200'
                              : 'bg-yellow-100 text-yellow-800 border-yellow-200'
                              }`}
                          >
                            {postCuenta.estado === 'completado' ? '✓ Completado' : '⏳ Borrador'}
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-x-4 gap-y-1">
                        <p className="text-xs text-gray-400">
                          📅 Creada: {new Date(postCuenta.fecha_creacion).toLocaleDateString('es-CO', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                        {postCuenta.fecha_actualizacion && (
                          <p className="text-xs text-purple-400 flex items-center gap-1">
                            <span>🔄</span> Modificada: {new Date(postCuenta.fecha_actualizacion).toLocaleDateString('es-CO', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm text-gray-600 mb-4">
                    <p>
                      <strong>Contrato:</strong> {postCuenta.numero_contrato}
                    </p>
                    <p className="line-clamp-1">
                      <strong>Evidencias:</strong> {postCuenta.rango_evidencias || 'Sin rango especificado'}
                    </p>
                    <p className="flex items-center gap-2">
                      <strong>📸 Conteo:</strong>
                      <span className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full font-bold text-xs">
                        {postCuenta.obligaciones?.reduce((acc, obl) => acc + (obl.evidencias?.length || 0), 0) || 0} cargadas
                      </span>
                    </p>
                  </div>

                  {operacionEnCurso[postCuenta.id] && (
                    <div className="flex items-center gap-2 mb-2 px-3 py-1.5 bg-purple-50 rounded-lg">
                      <span className="w-3.5 h-3.5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin"></span>
                      <span className="text-xs text-purple-700 font-medium">
                        {operacionEnCurso[postCuenta.id] === 'enviando-pdf' ? 'Enviando PDF por correo...' :
                         operacionEnCurso[postCuenta.id] === 'enviando-zip' ? 'Enviando backup por correo...' :
                         operacionEnCurso[postCuenta.id] === 'exportando' ? 'Exportando respaldo...' :
                         operacionEnCurso[postCuenta.id] === 'generando-pdf' ? 'Generando PDF...' :
                         operacionEnCurso[postCuenta.id] === 'duplicando' ? 'Duplicando post cuenta...' :
                         operacionEnCurso[postCuenta.id] === 'bloqueando' ? 'Bloqueando post cuenta...' :
                         operacionEnCurso[postCuenta.id] === 'desbloqueando' ? 'Desbloqueando post cuenta...' : 'Procesando...'}
                      </span>
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
                    <button
                      onClick={() => handleEditarPostCuenta(postCuenta.id, postCuenta.bloqueada, postCuentas.length - postCuentas.indexOf(postCuenta))}
                      className="flex-1 min-w-[80px] px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium transition"
                    >
                      ✏️ Editar
                    </button>
                    <button
                      onClick={() => duplicarPostCuenta(postCuenta.id)}
                      disabled={!!operacionEnCurso[postCuenta.id]}
                      className="flex-1 min-w-[90px] px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium transition disabled:opacity-50"
                      title="Duplicar esta post cuenta"
                    >
                      {operacionEnCurso[postCuenta.id] === 'duplicando' ? '⏳' : '📋'} Duplicar
                    </button>
                    <button
                      onClick={() => handleExportPostCuenta(postCuenta)}
                      disabled={!!operacionEnCurso[postCuenta.id]}
                      className="flex-1 min-w-[40px] px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition disabled:opacity-50"
                      title="Exportar respaldo completo (ZIP con anexos)"
                    >
                      {operacionEnCurso[postCuenta.id] === 'exportando' ? '⏳' : '⬇️'}
                    </button>
                    {postCuenta.bloqueada ? (
                      <button
                        onClick={() => desbloquearPostCuenta(postCuenta.id)}
                        disabled={!!operacionEnCurso[postCuenta.id]}
                        className="flex-1 min-w-[100px] px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 font-medium transition disabled:opacity-50"
                        title="Desbloquear para editar"
                      >
                        {operacionEnCurso[postCuenta.id] === 'desbloqueando' ? '⏳' : '🔓'} Desbloquear
                      </button>
                    ) : (
                      <button
                        onClick={() => bloquearPostCuenta(postCuenta.id)}
                        disabled={!!operacionEnCurso[postCuenta.id]}
                        className="flex-1 min-w-[90px] px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium transition disabled:opacity-50"
                        title="Bloquear para evitar ediciones"
                      >
                        {operacionEnCurso[postCuenta.id] === 'bloqueando' ? '⏳' : '🔒'} Bloquear
                      </button>
                    )}
                    <button
                      onClick={() => generarYDescargarPDFPostCuenta(postCuenta.id)}
                      disabled={!!operacionEnCurso[postCuenta.id]}
                      className="flex-1 min-w-[80px] px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition disabled:opacity-50"
                    >
                      {operacionEnCurso[postCuenta.id] === 'generando-pdf' ? '⏳' : '📥'} PDF
                    </button>
                    {isAdminOrSupport && (
                    <button
                      onClick={() => enviarPostCuentaPorEmail(postCuenta.id)}
                      disabled={!!operacionEnCurso[postCuenta.id]}
                      className="flex-1 min-w-[70px] px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 font-medium transition text-sm disabled:opacity-50"
                      title="Enviar Informe al correo"
                    >
                      {operacionEnCurso[postCuenta.id] === 'enviando-pdf' ? '⏳' : '📧'} PDF
                    </button>
                    )}
                    {isAdminOrSupport && (
                    <button
                      onClick={() => enviarBackupPostCuentaPorEmail(postCuenta.id)}
                      disabled={!!operacionEnCurso[postCuenta.id]}
                      className="flex-1 min-w-[70px] px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 font-medium transition text-sm disabled:opacity-50"
                      title="Enviar Respaldo (ZIP) al correo"
                    >
                      {operacionEnCurso[postCuenta.id] === 'enviando-zip' ? '⏳' : '📧'} ZIP
                    </button>
                    )}
                    <button
                      onClick={() => handlePreviewPostCuenta(postCuenta)}
                      disabled={!!operacionEnCurso[postCuenta.id]}
                      className="flex-1 min-w-[110px] px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 font-medium transition disabled:opacity-50"
                      title="Vista previa del PDF"
                    >
                      👁️ Vista Previa
                    </button>
                    <button
                      onClick={() => eliminarPostCuenta(postCuenta.id)}
                      disabled={!!operacionEnCurso[postCuenta.id]}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition disabled:opacity-50"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Modal de Vista Previa de PDF */}
      {previewConfig && (
        <PdfPreviewModal
          isOpen={!!previewConfig}
          onClose={() => setPreviewConfig(null)}
          {...previewConfig}
        />
      )}

      {/* Aviso emergente de validación (ej. faltan certificaciones) */}
      <AvisoValidacionModal aviso={avisoValidacion} onClose={() => setAvisoValidacion(null)} />

      {/* Modal: ¿duplicar con o sin evidencias? */}
      {duplicarTarget && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => { if (!duplicando) setDuplicarTarget(null); }}
        >
          <div
            className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6 animate-fade-in-up"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold text-blue-900 mb-1">
              📋 Duplicar {duplicarTarget.tipo === 'cuenta' ? 'Cuenta de Cobro' : 'Post Cuenta'}
            </h2>
            {duplicarTarget.mes && (
              <p className="text-sm text-gray-500 mb-4">{duplicarTarget.mes}</p>
            )}

            <p className="text-sm text-gray-700 mb-4">
              ¿Cómo quieres duplicarla?
            </p>

            {duplicando ? (
              <div className="flex flex-col items-center gap-2 py-6">
                <span className="animate-spin rounded-full h-8 w-8 border-4 border-indigo-500 border-t-transparent"></span>
                <p className="text-sm text-indigo-700 font-medium">Duplicando…</p>
              </div>
            ) : (
              <div className="space-y-3">
                <button
                  onClick={() => ejecutarDuplicado(true)}
                  className="w-full text-left px-4 py-3 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition"
                >
                  <div className="font-semibold text-indigo-900 text-sm">📎 Duplicar CON evidencias</div>
                  <div className="text-xs text-indigo-700 mt-0.5">
                    Copia todo tal cual: obligaciones, acciones realizadas y evidencias adjuntas.
                  </div>
                </button>
                <button
                  onClick={() => ejecutarDuplicado(false)}
                  className="w-full text-left px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition"
                >
                  <div className="font-semibold text-emerald-900 text-sm">🆕 Duplicar SIN evidencias</div>
                  <div className="text-xs text-emerald-700 mt-0.5">
                    Las obligaciones quedan en blanco (sin evidencias ni acciones) para cargar las del nuevo mes.
                  </div>
                </button>
                <p className="text-[11px] text-gray-400">
                  La copia quedará resaltada de primera en la lista. Entra a editarla para ajustar el mes y las fechas
                  (quedan iguales a las de la original).
                </p>
                <button
                  onClick={() => setDuplicarTarget(null)}
                  className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium transition"
                >
                  Cancelar
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal: Fusionar GC + Informe Post Cuenta firmado por el supervisor */}
      {mergeCuenta && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={cerrarModalMergeFirmado}
        >
          <div
            className="bg-white rounded-lg shadow-2xl max-w-lg w-full p-6 animate-fade-in-up"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold text-blue-900 mb-2 flex items-center gap-2">
              📎 Fusionar GC + Informe Post Cuenta firmado
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              {mergeCuenta.mes_cobro} — Contrato {mergeCuenta.numero_contrato}
            </p>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-lg p-4 mb-4">
              <p className="text-sm text-yellow-800">
                <strong>Recuerda:</strong> debes subir tu <strong>Informe Post Cuenta ya FIRMADO
                por tu supervisor</strong>. IMEC lo genera firmado solo por ti; descárgalo,
                envíalo a tu supervisor, espera su firma y súbelo aquí firmado para fusionarlo
                con la Cuenta de Cobro y sus evidencias.
              </p>
            </div>

            <label
              className={`relative block border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition ${
                mergeFile ? 'border-emerald-400 bg-emerald-50' : 'border-gray-300 bg-gray-50 hover:border-indigo-400 hover:bg-white'
              }`}
            >
              <input
                type="file"
                accept=".pdf,application/pdf"
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                onChange={(e) => setMergeFile(e.target.files?.[0] || null)}
              />
              <div className="text-4xl mb-2">{mergeFile ? '✅' : '📄'}</div>
              {mergeFile ? (
                <p className="text-sm font-medium text-emerald-700 break-all">{mergeFile.name}</p>
              ) : (
                <>
                  <p className="text-sm font-semibold text-gray-700">Subir Informe Post Cuenta firmado (PDF)</p>
                  <p className="text-xs text-gray-400 mt-1">Haz clic o arrastra el archivo aquí</p>
                </>
              )}
            </label>

            <div className="flex gap-3 mt-6">
              <button
                onClick={cerrarModalMergeFirmado}
                disabled={merging}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium transition disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={fusionarConIfpcFirmado}
                disabled={!mergeFile || merging}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium transition shadow disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                {merging ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                    Fusionando…
                  </>
                ) : (
                  <>📎 Fusionar y descargar</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
